import React, { useState } from "react";
import {
  Sidebar,
  Tree,
  BaseButton,
  SelectMulti,
} from "@velocity-ehs/component-library";
import { useTranslation } from "react-i18next";
import UsersService from "@velocity-ehs/user-utils-module";
import { useForm, Controller } from "react-hook-form";
import { useGetAvailableRoles } from "../../../hooks/useGetAvailableRoles";
import classes from "./PersonAssignRolesSlider.module.scss";
import { AssignRolesInput } from "../../../hooks/usePersonRolesMutation";

interface Props {
  showSlider: boolean;
  closeSliderCallback: () => void;
  assignRoleCallback: (values: any) => void;
}

const PersonAssignRolesSlider: React.FC<Props> = ({
  showSlider,
  closeSliderCallback,
  assignRoleCallback,
}) => {
  const { t } = useTranslation();
  const [selected, setSelected] = useState({});
  const { customerRoles, isFetching } = useGetAvailableRoles(
    UsersService.customer.id
  );
  const formMethods = useForm<AssignRolesInput>({
    mode: "onBlur",
    defaultValues: {
      roles: null,
      locations: null,
    },
  });

  const {
    control,
    handleSubmit,
    setError,
    setValue,
    getValues,
    reset,
    formState: { errors, isSubmitting },
  } = formMethods;

  const onSelect = (e: any) => {
    setError("locations", null);
    const previous = getValues("locations");
    setValue("locations", previous ? [...previous, e.node.id] : [e.node.id]);
  };

  const onUnselect = (e: any) => {
    const previous = getValues("locations");
    setValue(
      "locations",
      previous.filter((id) => id !== e.node.id)
    );
  };

  const onRemove = (node: any) => {
    setSelected((previous: any) => {
      const current = { ...previous };
      delete current[node.key];
      return current;
    });
    onUnselect({ node });
  };

  const onSubmit = async (data: AssignRolesInput) => {
    try {
      await assignRoleCallback(data);
      onClose();
    } catch (error) {}
  };

  const onClose = () => {
    reset();
    setSelected({});
    closeSliderCallback();
  };

  const footerButtons = () => {
    return (
      <>
        <BaseButton
          loading={isSubmitting}
          type="submit"
          form="personAssignRolesForm"
          label={t("roles_Assign")}
          className={classes.saveButton}
        />
        <BaseButton
          label={t("universal_Cancel")}
          className="p-button-text"
          onClick={onClose}
        />
      </>
    );
  };

  return (
    <Sidebar
      header={t("person_roles_assign_role")}
      visible={showSlider}
      onHide={onClose}
      footer={footerButtons()}
    >
      <form id="personAssignRolesForm" onSubmit={handleSubmit(onSubmit)}>
        <h4>{t("person_roles_select_Roles")}</h4>
        <SelectMulti
          errorMessage={errors.roles?.message}
          options={customerRoles}
          optionLabel="name"
          control={control}
          required
          name="roles"
          label={t("meta_role")}
          isLoading={isFetching}
        />
        <h4 style={{ marginTop: "24px" }}>
          {t("person_roles_select_locations")}
        </h4>
        <Controller
          control={control}
          name="locations"
          rules={{
            required: t("person_roles_location_required_message"),
          }}
          render={() => (
            <Tree
              isMultiSelect
              onSelect={onSelect}
              onUnselect={onUnselect}
              onSelectionChange={(e) => setSelected(e.value)}
              onRemove={onRemove}
              selectionKeys={selected}
              errorMessage={errors.locations?.message}
            />
          )}
        />
      </form>
    </Sidebar>
  );
};

export default PersonAssignRolesSlider;
